import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Collapse,
  Fab,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Feedback as FeedbackIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import api from '../../utils/api';
import { useIsOffline } from '../../offline';

const FeedbackWidget: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  // Feedback isn't a replicated entity — offline, block the send (false
  // whenever offline mode is off).
  const isOffline = useIsOffline();

  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [aiAssistantExpanded, setAiAssistantExpanded] = useState(false);
  
  const location = useLocation();

  // Check if AI Assistant is expanded
  React.useEffect(() => {
    const checkAiAssistant = () => {
      const aiAssistant = document.querySelector('.ai-assistant');
      if (aiAssistant) {
        const height = aiAssistant.getBoundingClientRect().height;
        setAiAssistantExpanded(height > 60); // More than just the minimized button
      }
    };
    
    checkAiAssistant();
    const interval = setInterval(checkAiAssistant, 300);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsSubmitting(true);
    
    try {
      await api.post('/feedback', {
        message,
        page: location.pathname,
      });
      
      setMessage('');
      setShowSuccess(true);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="feedback"
        className="feedback-button"
        onClick={() => setIsOpen(!isOpen)}
        size={isMobile ? 'medium' : 'large'}
        sx={{
          position: 'fixed',
          bottom: { xs: aiAssistantExpanded ? 140 : 16, sm: 20 },
          left: { xs: 16, sm: 288 },
          zIndex: 1300,
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          minHeight: 0,
          bgcolor: '#C9772B',
          '&:hover': { bgcolor: '#e08f43' },
          transition: 'bottom 0.3s ease-in-out',
          boxShadow: '0 8px 30px rgba(139,94,52,0.3)',
        }}
      >
        <FeedbackIcon fontSize={isMobile ? 'small' : 'medium'} />
      </Fab>

      {/* Feedback Panel */}
      <Collapse in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: aiAssistantExpanded ? 200 : 76, sm: 74 },
            left: { xs: 16, sm: 288 },
            width: { xs: 'calc(100vw - 32px)', sm: 300 },
            maxWidth: { xs: 340, sm: 300 },
            zIndex: 1300,
            p: { xs: 1.5, sm: 2 },
            bgcolor: '#fcfcfd',
            border: '1px solid rgba(139,94,52,0.2)',
            borderRadius: 2,
            transition: 'bottom 0.3s ease-in-out',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 1.5, sm: 2 }}>
            <Typography 
              variant="h6" 
              color="#C9772B" 
              fontFamily='"Crimson Text", serif'
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              Share Feedback
            </Typography>
            <IconButton 
              onClick={() => setIsOpen(false)} 
              size="small"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={"Tell us what you think about this page..."}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&.Mui-focused fieldset': {
                    borderColor: '#C9772B',
                  },
                },
              }}
            />

            {isOffline && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Feedback needs an internet connection.
              </Typography>
            )}

            <Box display="flex" justifyContent="flex-end" mt={{ xs: 1.5, sm: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !message.trim() || isOffline}
                endIcon={<SendIcon fontSize={isMobile ? 'small' : 'medium'} />}
                sx={{
                  minHeight: 44,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  bgcolor: '#C9772B',
                  '&:hover': {
                    bgcolor: '#e08f43',
                  },
                }}
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Collapse>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          Thank you for your feedback!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={4000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to submit feedback. Please try again.
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackWidget; 